import { useState } from "react";
import { createPortal } from "react-dom";
import { SkipForward } from "lucide-react";

import type { OnboardingMode } from "@/hooks/useOnboardingStatus";

interface SkipOnboardingButtonProps {
  /** Onboarding mode from `useOnboardingStatus`. Visible ONLY on `"onboarding"`. */
  mode: OnboardingMode;
  /** Fired after the user confirms. One-way: there is no un-skip affordance. */
  onSkip: () => void;
}

/**
 * The escape hatch out of the first-run ritual (rnk-u55j).
 *
 * An icon button matching the header cluster it sits in (Workflow / sound /
 * board toggles): same 10x10 rounded-full chrome, lucide glyph, `title`
 * tooltip and `sr-only` label. The glyph is `SkipForward` (the media
 * skip-to-end symbol) — the established convention for "skip intro/setup",
 * chosen over FastForward (reads "speed up, keep going") and X (reads
 * "dismiss this chrome"). Icon-only is acceptable for a one-way action ONLY
 * because the confirm dialog below carries the full consequence copy; the
 * icon never commits anything by itself.
 *
 * Owns its own visibility so the rule lives in ONE place rather than at every
 * mount site, and owns its confirm dialog because skipping is irreversible.
 *
 * COPY RULES (binding, this whole component): no em-dashes, no dates, no
 * promises about features that do not exist yet. The user reading this is
 * mid-conversation with an agent whose own prompt bans the same things.
 */
export function SkipOnboardingButton({ mode, onSkip }: SkipOnboardingButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (mode !== "onboarding") return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        title="Skip setup"
        className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-black/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
      >
        <SkipForward className="h-4 w-4" />
        <span className="sr-only">Skip setup</span>
      </button>

      {confirmOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setConfirmOpen(false)}
            />
            <div className="relative z-10 mx-4 w-full max-w-md rounded-3xl border border-black bg-white p-8 dark:border-white/20 dark:bg-[#2A2A2A]">
              <h2 className="mb-4 font-heading text-2xl font-bold text-black dark:text-white">
                Skip setup?
              </h2>
              <p className="mb-3 text-sm text-black/70 dark:text-white/70">
                Clawbert stops asking setup questions and starts working with
                whatever it already knows about you.
              </p>
              <p className="mb-3 text-sm text-black/70 dark:text-white/70">
                You can tell it more about yourself any time, just by saying so
                in chat. Setup itself does not come back.
              </p>
              <p className="mb-6 text-sm text-black/50 dark:text-white/50">
                Clawbert restarts to pick up the change, so give it a moment
                before your next message.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  className="rounded-full border border-black/10 px-5 py-2.5 text-sm font-bold text-black/70 transition hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
                >
                  Keep going
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmOpen(false);
                    onSkip();
                  }}
                  className="rounded-full bg-brand-coral px-5 py-2.5 text-sm font-bold text-white transition hover:brightness-95"
                >
                  Skip setup
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
